import React, { memo } from 'react';

// Helper to determine if color is light or dark
const isLightColor = (color) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128;
};

// Shape components
const RectangleShape = ({ width, height, color, isSelected }) => (
  <rect
    x={0}
    y={0}
    width={width}
    height={height}
    fill={color}
    stroke={isSelected ? '#1d4ed8' : '#374151'}
    strokeWidth={isSelected ? 3 : 2}
    rx={4}
    ry={4}
  />
);

const RoundedShape = ({ width, height, color, isSelected }) => (
  <rect
    x={0}
    y={0}
    width={width}
    height={height}
    fill={color}
    stroke={isSelected ? '#1d4ed8' : '#374151'}
    strokeWidth={isSelected ? 3 : 2}
    rx={height / 2}
    ry={height / 2}
  />
);

const DiamondShape = ({ width, height, color, isSelected }) => {
  const points = [
    `${width / 2},0`,
    `${width},${height / 2}`,
    `${width / 2},${height}`,
    `0,${height / 2}`
  ].join(' ');

  return (
    <polygon
      points={points}
      fill={color}
      stroke={isSelected ? '#1d4ed8' : '#374151'}
      strokeWidth={isSelected ? 3 : 2}
    />
  );
};

const ParallelogramShape = ({ width, height, color, isSelected }) => {
  const skew = 20;
  const points = [
    `${skew},0`,
    `${width},0`,
    `${width - skew},${height}`,
    `0,${height}`
  ].join(' ');

  return (
    <polygon
      points={points}
      fill={color}
      stroke={isSelected ? '#1d4ed8' : '#374151'}
      strokeWidth={isSelected ? 3 : 2}
    />
  );
};

const TextShape = ({ width, height, isSelected }) => (
  // Text node has no background, only shows selection outline when selected
  <rect
    x={0}
    y={0}
    width={width}
    height={height}
    fill="transparent"
    stroke={isSelected ? '#1d4ed8' : 'transparent'}
    strokeWidth={isSelected ? 1 : 0}
    strokeDasharray={isSelected ? '4,2' : 'none'}
    rx={2}
    ry={2}
  />
);

const Node = memo(({
  node,
  isSelected,
  isConnecting,
  isConnectingFrom,
  onSelect,
  onDoubleClick,
  onMouseDown,
  onConnect
}) => {
  const { id, x, y, text, type, color, width, height, notes } = node;
  // Text nodes always use dark text (no background), others depend on color
  const textColor = type === 'text' ? '#374151' : (isLightColor(color) ? '#1f2937' : '#ffffff');
  const hasNotes = notes && notes.trim().length > 0;

  const handleClick = (e) => {
    e.stopPropagation();
    if (isConnecting && !isConnectingFrom) {
      onConnect?.(id);
    } else {
      // Pass shift key state for multi-select
      onSelect?.(id, e.shiftKey);
    }
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    onDoubleClick?.(id);
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // Only left click
    e.stopPropagation();
    onMouseDown?.(e, id, x, y);
  };

  const renderShape = () => {
    const props = { width, height, color, isSelected: isSelected || isConnectingFrom };

    switch (type) {
      case 'rounded':
        return <RoundedShape {...props} />;
      case 'diamond':
        return <DiamondShape {...props} />;
      case 'parallelogram':
        return <ParallelogramShape {...props} />;
      case 'text':
        return <TextShape {...props} />;
      case 'rectangle':
      default:
        return <RectangleShape {...props} />;
    }
  };

  // Render multiline text
  const renderText = () => {
    const lines = text.split('\n');
    const lineHeight = 18;
    const startY = height / 2 - ((lines.length - 1) * lineHeight) / 2;

    return lines.map((line, index) => (
      <tspan
        key={index}
        x={width / 2}
        y={startY + index * lineHeight}
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {line}
      </tspan>
    ));
  };

  return (
    <g
      transform={`translate(${x}, ${y})`}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onMouseDown={handleMouseDown}
      style={{ cursor: isConnecting && !isConnectingFrom ? 'crosshair' : 'move' }}
      className="node-group"
    >
      {renderShape()}

      {/* Selection highlight glow */}
      {isConnectingFrom && (
        <rect
          x={-4}
          y={-4}
          width={width + 8}
          height={height + 8}
          fill="none"
          stroke="#3b82f6"
          strokeWidth={2}
          strokeDasharray="5,5"
          rx={type === 'rounded' ? (height + 8) / 2 : 6}
          ry={type === 'rounded' ? (height + 8) / 2 : 6}
          className="animate-pulse"
        />
      )}

      {/* Connecting target indicator */}
      {isConnecting && !isConnectingFrom && (
        <rect
          x={-2}
          y={-2}
          width={width + 4}
          height={height + 4}
          fill="none"
          stroke="#10b981"
          strokeWidth={2}
          rx={type === 'rounded' ? (height + 4) / 2 : 4}
          ry={type === 'rounded' ? (height + 4) / 2 : 4}
          opacity={0.5}
        />
      )}

      {/* Node text */}
      <text
        fill={textColor}
        fontSize="14"
        fontWeight="500"
        fontFamily="Inter, system-ui, sans-serif"
        pointerEvents="none"
      >
        {renderText()}
      </text>

      {/* Connection points */}
      {isSelected && (
        <>
          {/* Top */}
          <circle cx={width / 2} cy={0} r={5} fill="#3b82f6" stroke="#fff" strokeWidth={2} />
          {/* Bottom */}
          <circle cx={width / 2} cy={height} r={5} fill="#3b82f6" stroke="#fff" strokeWidth={2} />
          {/* Left */}
          <circle cx={0} cy={height / 2} r={5} fill="#3b82f6" stroke="#fff" strokeWidth={2} />
          {/* Right */}
          <circle cx={width} cy={height / 2} r={5} fill="#3b82f6" stroke="#fff" strokeWidth={2} />
        </>
      )}

      {/* Notes indicator */}
      {hasNotes && (
        <g transform={`translate(${width - 8}, -8)`}>
          <circle r={10} fill="#f59e0b" stroke="#fff" strokeWidth={2} />
          <path
            d="M-4 -2h8M-4 1h4"
            stroke="#fff"
            strokeWidth={1.5}
            strokeLinecap="round"
          />
          <title>{notes}</title>
        </g>
      )}
    </g>
  );
});

Node.displayName = 'Node';

export default Node;
